import type { ReactNode } from "react";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import classNames from "classnames";
import { FaAnglesLeft, FaAnglesRight } from "react-icons/fa6";

import useApiClient from "@/hooks/useApiClient";
import UserContext from "@/contexts/user";
import TopNav from "@/components/topNav";
import SubNav from "@/components/subNav";
import MobileNav from "@/components/mobileNav";
import {
  getActiveNavSection,
  getVisibleNavItems,
} from "@/components/navConfig";

export default function SignedInLayout({ children }: { children: ReactNode }) {
  const { user } = useContext(UserContext);
  const api = useApiClient();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [isSubNavCollapsed, setIsSubNavCollapsed] = useState(false);
  const activeSection = getActiveNavSection(pathname, user);
  const hasActiveSubNavSection =
    !!activeSection && getVisibleNavItems(activeSection, user).length > 0;

  useEffect(() => {
    // The overlay only needs to close after route transitions.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsMobileNavOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = isMobileNavOpen ? "hidden" : "";

    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileNavOpen]);

  const handleSignOut = async () => {
    try {
      await api.auth.authCookieLogout();
    } catch (error) {
      console.error("Error during signout: ", error);
    }

    await navigate({ to: "/auth/signIn" });
  };

  return (
    <div className="flex min-h-screen flex-col bg-zinc-50">
      <TopNav
        isMobileNavOpen={isMobileNavOpen}
        onOpenMobileNav={() => setIsMobileNavOpen((prev) => !prev)}
        onSignOut={handleSignOut}
      />

      <MobileNav
        isOpen={isMobileNavOpen}
        onClose={() => setIsMobileNavOpen(false)}
        onSignOut={handleSignOut}
      />

      <div className="flex flex-1 flex-row pt-16">
        {hasActiveSubNavSection && (
          <div className="hidden lg:block">
            <div
              aria-hidden={isSubNavCollapsed}
              className={classNames(
                "fixed top-16 h-[calc(100vh-4rem)] overflow-hidden border-zinc-200 bg-white transition-[width,border-width] duration-300 ease-in-out",
                isSubNavCollapsed ? "w-0 border-r-0" : "w-52 border-r"
              )}
            >
              <div className="w-52">
                {!isSubNavCollapsed && <SubNav />}
              </div>
            </div>

            <button
              type="button"
              onClick={() => setIsSubNavCollapsed((prev) => !prev)}
              title={isSubNavCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              aria-label={
                isSubNavCollapsed ? "Expand sidebar" : "Collapse sidebar"
              }
              className={classNames(
                "fixed top-[4.5rem] z-[115] flex h-6 w-6 items-center justify-center rounded-full border border-zinc-200 bg-white text-zinc-400 shadow-sm transition-[left,border-color,color,transform] duration-300 ease-in-out after:absolute after:-inset-2 after:content-[''] hover:border-zinc-300 hover:text-zinc-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-100 active:scale-[0.96]",
                isSubNavCollapsed ? "left-2" : "left-[calc(13rem-0.75rem)]"
              )}
            >
              <span
                className={classNames(
                  "absolute inset-0 flex items-center justify-center transition-[opacity,filter,transform] duration-200 ease-[cubic-bezier(0.2,0,0,1)]",
                  isSubNavCollapsed
                    ? "scale-100 opacity-100 blur-0"
                    : "scale-[0.25] opacity-0 blur-sm"
                )}
              >
                <FaAnglesRight size={9} />
              </span>
              <span
                className={classNames(
                  "absolute inset-0 flex items-center justify-center transition-[opacity,filter,transform] duration-200 ease-[cubic-bezier(0.2,0,0,1)]",
                  isSubNavCollapsed
                    ? "scale-[0.25] opacity-0 blur-sm"
                    : "scale-100 opacity-100 blur-0"
                )}
              >
                <FaAnglesLeft size={9} />
              </span>
            </button>
          </div>
        )}

        <main
          className={classNames(
            "min-h-[calc(100vh-4rem)] flex-1 bg-zinc-50 transition-[margin-left] duration-300 ease-in-out",
            hasActiveSubNavSection
              ? isSubNavCollapsed
                ? "lg:ml-0"
                : "lg:ml-52"
              : "lg:ml-0"
          )}
        >
          {children}
        </main>
      </div>
    </div>
  );
}
